import { create } from "zustand";
import instance from "@/lib/axios";
import { organizationStorage, resolveOrgSlug } from "@/lib/organization";
import { formatError } from "@/lib/error";

export interface OrganizationTimezone {
  timezone: string;
}

interface OrganizationState {
  organizationTimezone: OrganizationTimezone | null;
  orgSlug: string | null;
  /** True once a slug resolution has been attempted, successfully or not. */
  orgSlugAttempted: boolean;
  isLoading: boolean;
  error: string | null;
  fetchTimezone: () => Promise<void>;
  fetchOrgSlug: () => Promise<void>;
  clearOrganizationStore: () => void;
}


export const useOrganizationStore = create<OrganizationState>()((set, get) => ({
  organizationTimezone: organizationStorage.getTimezone(),
  orgSlug: resolveOrgSlug(),
  orgSlugAttempted: false,
  isLoading: false,
  error: null,

  fetchTimezone: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await instance.get<OrganizationTimezone>(
        "/api/organization/timezone/",
      );
      organizationStorage.setTimezone(response.data);
      set({ organizationTimezone: response.data, isLoading: false });
    } catch (err: unknown) {
      set({
        error: formatError(err, "Failed to fetch timezone"),
        isLoading: false,
      });
    }
  },

  // Resolved locally, by the same rule axios uses to build URLs (T-158). The
  // old `/api/organization/profile/` lookup could never work: without a slug
  // the URL matches no route, and with one it was redundant.
  fetchOrgSlug: async () => {
    if (get().orgSlugAttempted) return;
    set({ orgSlug: resolveOrgSlug(), orgSlugAttempted: true });
  },

  clearOrganizationStore: () => {
    organizationStorage.resetTimezone();
    organizationStorage.resetOrgSlug();
    set({
      organizationTimezone: null,
      orgSlug: null,
      orgSlugAttempted: false,
      error: null,
    });
  },
}));
